import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';

// Icônes
import { Award, Trophy, Medal, Crown, ChevronDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// --- Données des vraies Distinctions ---
const distinctionsList = [
    { title: "Prix d'Honneur et de Leadership en Stratégie et Sécurité", description: "Reconnait et valorise les pratiques innovantes en stratégie et en sécurité, et salue le dynamisme des personnalités qui mettent en œuvre la politique sécuritaire des États." },
    { title: "Prix Pan-Africain des Meilleures Pratiques Managériales", description: "Récompense les meilleures pratiques managériales innovantes multisectorielles sur le plan sous-régional et continental." },
    { title: "Prix Pan-Africain des Meilleures Pratiques Judiciaires Innovantes", description: "Récompense les meilleures pratiques judiciaires innovantes ainsi que la compétence et le parcours exceptionnel des magistrats et auxiliaires de justice." },
    { title: "Prix Pan-Africain des Meilleures Pratiques Médicales", description: "Récompense les meilleures pratiques médicales, et salue l'innovation et la compétence des professionnels de la santé." },
    { title: "Prix Panafricain de la Meilleure Qualité", description: "Vise à saluer la démarche d'excellence durable et de performance instituée au sein des entreprises citoyennes, en mettant en exergue l'innovation et le respect des normes." },
    { title: "Prix Pan-Africain de l'Excellence Politique", description: "Récompense les valeurs et la vision politique des acteurs politiques sur le plan sous-régional et continental." },
    { title: "Prix International de l'Environnement", description: "Récompense les contributions exceptionnelles en faveur de la protection de l'environnement et du développement durable." },
    { title: "Prix Pan-Africain d'Excellence Politique et de Leadership Jeunesse", description: "Récompense le courage et l'engagement politique des jeunes entrepreneurs politiques." },
    { title: "Prix de l'Intégrité", description: "Récompense les personnalités et institutions aux valeurs exemplaires qui sont des sources d'inspiration." },
    { title: "Prix d'Excellence de la Lutte contre la Corruption", description: "Reconnait et encourage la détermination des individus et organisations ayant pris des mesures efficaces pour combattre la corruption." },
    { title: "Prix d'Excellence de la Fonction Publique", description: "Récompense les fonctionnaires et agents de l'État qui font preuve d'excellence, de professionnalisme et d'innovation." },
    { title: "Prix Pan-Africain du Meilleur Artisan du Développement", description: "Récompense annuellement les contributions exceptionnelles des individus et institutions dans le processus du développement du continent." },
    { title: "Prix d'Excellence en Leadership Féminin", description: "Récompense le leadership et les parcours exceptionnels des femmes qui excellent dans leur mission." },
    { title: "Prix du Mérite Panafricain", description: "Récompense le parcours exceptionnel et les valeurs humaines des personnalités qui se démarquent par leur exemplarité." },
    { title: "Prix « African Entrepreneurship Prize »", description: "Récompense l'esprit entrepreneurial, l'ingéniosité et les initiatives à fort impact sur le développement du continent." },
    { title: "Prix des Bonnes Pratiques Comptables", description: "Vise à reconnaître le parcours exceptionnel des professionnels de l'expertise comptable et leurs contributions au rayonnement de la profession." },
    { title: "Prix des Meilleures Pratiques Immobilières Innovantes", description: "Reconnait et encourage la démarche d'excellence et d'innovation dans les prestations immobilières." },
    { title: "Prix des Meilleures Pratiques Professionnelles Innovantes", description: "Reconnait et encourage les bonnes pratiques professionnelles au sein des entreprises, organisations et administrations." },
    { title: "Prix du Manager Sportif Africain de l'Année", description: "Vise à saluer le parcours exceptionnel, l'innovation et les réalisations sportives impactantes des managers d'organisations sportives." },
    { title: "Prix Panafricain de la Paix et de la Sécurité", description: "Vise à saluer les efforts des institutions ayant consenti des efforts considérables en faveur de la sécurité et du maintien de la paix." },
    { title: "Prix Panafricain du Développement", description: "Récompense le parcours exceptionnel des entrepreneurs Africains et leurs réalisations impactantes." },
    { title: "Prix du Développement et de l'Innovation", description: "Récompense annuellement les réalisations innovantes en faveur du développement de l'Afrique." },
    { title: "Distinction « Personnalité de l'Année »", description: "Rend hommage aux personnalités ayant marqué positivement l'année sur le plan continental par leur vision, leurs réalisations et leurs valeurs." },
    { title: "Prix d'Excellence des Armées", description: "Récompense le leadership exemplaire ainsi que des contributions positives et substantielles en faveur de l'armée." },
];

// Composant pour l'accordéon
const AccordionItem = ({ title, description, isOpen, onClick }: { title: string, description: string, isOpen: boolean, onClick: () => void }) => (
    <div className="border-b border-white/10">
        <button
            onClick={onClick}
            className="w-full flex justify-between items-center text-left py-6 px-4 md:px-6 hover:bg-white/5 transition-colors duration-300"
        >
            <h3 className="text-lg md:text-xl font-semibold text-white">{title}</h3>
            <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
            >
                <ChevronDown className="w-6 h-6 text-[#d2ab6a] flex-shrink-0" />
            </motion.div>
        </button>
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="overflow-hidden"
                >
                    <p className="text-white/80 leading-relaxed pb-6 px-4 md:px-6">
                        {description}
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

export const DistinctionsPage: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <PublicLayout>
            <SEOMetaTags
                title="Nos Distinctions - International Committee of Excellence (ICE)"
                description="Découvrez le catalogue complet des prix et distinctions décernés par l'ICE pour récompenser l'excellence à travers l'Afrique."
            />

            <div className="bg-[#0d1321] text-white">

                {/* --- Section Héros --- */}
                <section
                    className="relative py-32 md:py-48 flex items-center justify-center text-center"
                    style={{
                        backgroundImage: 'url(https://ice-inter.com/wp-content/uploads/2023/10/3bfb0309-52f8-4ad0-85e0-c43819d63c9e-1.jpg)',
                        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'
                    }}
                >
                    <div className="absolute inset-0 bg-[#0d1321]/80 backdrop-blur-sm"></div>
                    <div className="relative z-10 container mx-auto px-4">
                        <div className="flex justify-center mb-6">
                            <div className="text-[#d2ab6a] w-20 h-20 flex items-center justify-center">
                                <Trophy className="w-full h-full" />
                            </div>
                        </div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                            className="text-4xl md:text-6xl font-bold text-white mb-4"
                        >
                            Catalogue des <span className="text-[#d2ab6a]">Distinctions</span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto"
                        >
                            Découvrez notre collection complète de distinctions qui célèbrent l'excellence africaine dans tous les domaines d'activité.
                        </motion.p>
                    </div>
                </section>

                {/* --- Section Présentation des Distinctions --- */}
                <section className="py-20">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                                L'<span className="text-[#d2ab6a]">Excellence</span> Récompensée
                            </h2>
                            <p className="text-lg text-white/80 leading-relaxed mb-12">
                                L'International Committee of Excellence (ICE) décerne 24 distinctions prestigieuses pour honorer
                                les personnalités, organisations et initiatives qui contribuent au développement et à la promotion
                                de l'excellence sur le continent africain et dans sa diaspora.
                            </p>
                        </div>

                        {/* --- Accordéon des Distinctions --- */}
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                                {distinctionsList.map((distinction, index) => (
                                    <AccordionItem
                                        key={index}
                                        title={distinction.title}
                                        description={distinction.description}
                                        isOpen={openIndex === index}
                                        onClick={() => handleToggle(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* --- Section Statistiques --- */}
                <section className="py-20 bg-white/5">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                Nos <span className="text-[#d2ab6a]">Distinctions</span> en Chiffres
                            </h2>
                        </div>

                        <div className="grid md:grid-cols-4 gap-8 text-center">
                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <div className="text-[#d2ab6a] w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Award className="w-full h-full" />
                                </div>
                                <div className="text-4xl font-bold text-[#d2ab6a] mb-2">24</div>
                                <p className="text-white/70">Distinctions</p>
                            </div>

                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <div className="text-[#d2ab6a] w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Trophy className="w-full h-full" />
                                </div>
                                <div className="text-4xl font-bold text-[#d2ab6a] mb-2">8</div>
                                <p className="text-white/70">Domaines</p>
                            </div>

                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <div className="text-[#d2ab6a] w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Medal className="w-full h-full" />
                                </div>
                                <div className="text-4xl font-bold text-[#d2ab6a] mb-2">100%</div>
                                <p className="text-white/70">Africaines</p>
                            </div>

                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <div className="text-[#d2ab6a] w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Crown className="w-full h-full" />
                                </div>
                                <div className="text-4xl font-bold text-[#d2ab6a] mb-2">1</div>
                                <p className="text-white/70">Objectif</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* --- CTA vers la page Lauréats --- */}
                <section className="py-20">
                    <div className="container mx-auto px-4 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                Découvrez les <span className="text-[#d2ab6a]">Lauréats</span>
                            </h2>
                            <p className="text-white/80 max-w-2xl mx-auto mb-8">
                                Explorez les profils inspirants des personnalités qui ont été honorées par nos distinctions prestigieuses.
                            </p>
                            <Link
                                to="/nos-laureats"
                                className="bg-[#d2ab6a] text-[#0d1321] hover:bg-[#c19b59] inline-flex items-center space-x-2 px-8 py-4 text-lg font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                                <span>Voir Nos Lauréats</span>
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </motion.div>
                    </div>
                </section>
            </div>
        </PublicLayout>
    );
};

export default DistinctionsPage;

